import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { getMediaOptions, useProjectMedia } from '@/api/media';
import { closeModal, openModal } from '@/components/modals/modals.utils';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_IMAGE_INPUT_TYPE,
  uploadImagesSchema,
} from '@/features/estate-media/schema/upload-media';

import { useDeleteImage } from '../api/delete-image';
import { useUploadImage } from '../api/upload-image';
import { MediaData } from './media-data';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/media/images'
);

export const MediaImagesDashboard = () => {
  const { projectId } = route.useParams();

  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { data } = useProjectMedia({ id: projectId });

  const images = Object.values(data.images).flat();

  const { mutateAsync: deleteImage } = useDeleteImage({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
        toast.success(t('successfullyAddedImage', { autoClose: 100 }));
      },
    },
  });

  const { mutateAsync: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey }),
    },
  });

  const handleDelete = (ids: string[], callback = () => {}) => {
    openModal('confirmation-modal', {
      title: `${t('delete')} ${t('image')} `,
      description: t('confirmDeleteDocument'),
      confirmationText: t('delete'),
      onSubmit: async () => {
        await deleteImage({ projectId, imageIds: ids });
        callback();
      },
    });
  };

  const submitHandler = async (data: UploadMediaModalType) => {
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadImage({ images: dataTransfer.files, projectId });
    closeModal();
  };

  const handleOpenModal = () => {
    openModal('upload-media', {
      submitHandler,
      acceptedType: ACCEPTED_IMAGE_INPUT_TYPE,
      acceptedMediaSchema: uploadImagesSchema(),
    });
  };

  const openThumbnailModal = async () => {
    openModal('select-thumbnail', { id: projectId, type: 'project', images });
  };

  return (
    <MediaData
      data={data.images}
      onUpload={handleOpenModal}
      handleDelete={handleDelete}
      onChooseThumbnail={openThumbnailModal}
      isThumbnailDisabled={!images.length}
      page="images"
      type="project"
      projectId={projectId}
    />
  );
};
